import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Button,
  Card,
  Empty,
  ErrorState,
  Icon,
  LevelBadge,
  Loading,
  Pill,
  Segmented,
} from '../components/ui';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import {
  age,
  money,
  moneyCompact,
  number,
  pricingLabel,
  stageLabel,
  stageTone,
} from '../lib/format';
import type {
  ApplicationListItem,
  ApplicationStage,
  Paged,
  PipelineSummary,
} from '../lib/types';

type Filter = 'all_open' | 'needs_action' | 'in_underwriting' | 'boarded' | 'all';
type View = 'table' | 'board';

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all_open', label: 'All open' },
  { value: 'needs_action', label: 'Needs action' },
  { value: 'in_underwriting', label: 'In underwriting' },
  { value: 'boarded', label: 'Boarded' },
];

/** The strip along the top, in the order the design lays it out. */
const STAGE_CELLS: Array<{ stage: ApplicationStage; label: string; color: string }> = [
  { stage: 'draft', label: 'Draft', color: 'var(--neutral-fg)' },
  { stage: 'submitted', label: 'Submitted', color: 'var(--chart-1)' },
  { stage: 'in_underwriting', label: 'In underwriting', color: 'var(--chart-1)' },
  { stage: 'conditions', label: 'Conditions', color: 'var(--warn-fg)' },
  { stage: 'boarding', label: 'Approved, boarding', color: 'var(--ok-fg)' },
  { stage: 'declined', label: 'Declined', color: 'var(--danger-fg)' },
];

const PAGE_SIZE = 12;

export function Pipeline() {
  const { user, can } = useAuth();
  const [params, setParams] = useSearchParams();

  const filter = (params.get('filter') as Filter) ?? 'all_open';
  const view = (params.get('view') as View) ?? 'table';
  const stage = params.get('stage') as ApplicationStage | null;
  const page = Number(params.get('page') ?? 0);

  const [search, setSearch] = useState(params.get('search') ?? '');
  const query = params.get('search') ?? '';

  const listPath = useMemo(() => {
    const q = new URLSearchParams();
    q.set('filter', filter);
    if (stage) q.set('stage', stage);
    if (query) q.set('search', query);
    q.set('limit', String(PAGE_SIZE));
    q.set('offset', String(page * PAGE_SIZE));
    return `/applications?${q.toString()}`;
  }, [filter, stage, query, page]);

  const list = useApi<Paged<ApplicationListItem>>(listPath);
  const summary = useApi<PipelineSummary>('/applications/summary');

  function update(next: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    // Any filter change resets pagination — otherwise you land on an empty page.
    if (!('page' in next)) p.delete('page');
    setParams(p);
  }

  const total = list.data?.total ?? 0;
  const shown = list.data?.items.length ?? 0;
  const from = page * PAGE_SIZE;

  return (
    <>
      <PageHeader
        breadcrumb={['Portfolio', 'Applications']}
        title="Pipeline"
        actions={
          <>
            <Button icon={Icon.download({ size: 14 })}>Export CSV</Button>
            {can('application.write') && (
              <Link to="/applications/new">
                <Button variant="primary" icon={Icon.plus({ size: 14 })}>
                  New application
                </Button>
              </Link>
            )}
          </>
        }
      />

      <ScopeBar
        action={
          summary.data ? (
            <span>
              Total in flight{' '}
              <strong>{moneyCompact(summary.data.inFlightMonthlyVolumeCents)} / mo</strong>
            </span>
          ) : undefined
        }
      >
        {user?.level === 'L4' ? (
          <>Showing your own applications. Agents see only what they wrote.</>
        ) : (
          <>
            Showing every application from <strong>{user?.nodeName}</strong> down. Agents
            see only their own.
          </>
        )}
      </ScopeBar>

      <PageBody>
        {summary.data && (
          <Card padded={false}>
            <div className="card__header" style={{ padding: '16px 20px 0', marginBottom: 12 }}>
              <h2 className="card__title">
                {number(summary.data.openTotal)} open applications
              </h2>
              <span className="card__meta">rolling {summary.data.windowDays} days</span>
            </div>
            <div className="stagestrip">
              {STAGE_CELLS.map((c) => (
                <button
                  key={c.stage}
                  type="button"
                  className={`stagecell ${stage === c.stage ? 'is-active' : ''}`}
                  onClick={() =>
                    update({
                      stage: stage === c.stage ? null : c.stage,
                      filter: stage === c.stage ? filter : 'all',
                    })
                  }
                  aria-pressed={stage === c.stage}
                >
                  <span
                    className="stagecell__rule"
                    style={{ background: c.color }}
                    aria-hidden="true"
                  />
                  <span className="stagecell__label">{c.label}</span>
                  <span className="stagecell__count">
                    {summary.data!.byStage[c.stage] ?? 0}
                  </span>
                </button>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <div className="toolbar">
            <Segmented
              label="Pipeline filter"
              value={filter}
              onChange={(v) => update({ filter: v, stage: null })}
              options={FILTERS}
            />
            <span className="spacer" />
            <Segmented
              label="View"
              value={view}
              onChange={(v) => update({ view: v })}
              options={[
                { value: 'table', label: 'Table' },
                { value: 'board', label: 'Board' },
              ]}
            />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                update({ search: search || null });
              }}
            >
              <input
                className="input"
                type="search"
                placeholder="Find a merchant or reference"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 240 }}
              />
            </form>
          </div>

          {list.loading && <Loading />}
          {list.error && <ErrorState error={list.error} onRetry={list.reload} />}

          {list.data && list.data.items.length === 0 && (
            <Empty
              title="No applications match"
              detail={
                query
                  ? `Nothing matches “${query}” in your scope.`
                  : 'Nothing sits in this stage right now.'
              }
            />
          )}

          {list.data && list.data.items.length > 0 && view === 'table' && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Application</th>
                    <th>Submitted by</th>
                    <th>Stage</th>
                    <th className="num">Monthly vol.</th>
                    <th>Pricing</th>
                    <th className="num">Updated</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <div className="cell-primary">{a.dba || 'Untitled draft'}</div>
                        <div className="cell-sub">
                          {a.reference} · MCC {a.mcc || '—'}
                        </div>
                      </td>
                      <td>
                        <span className="cell-person">
                          {a.submittedByUserId === user?.userId ? 'You' : a.submittedByName}
                          <LevelBadge level={a.submittedByLevel} />
                        </span>
                      </td>
                      <td>
                        <Pill tone={stageTone(a.stage)}>
                          {a.blockedOn && a.stage === 'conditions'
                            ? `Conditions · ${a.blockedOn.toLowerCase()}`
                            : stageLabel(a.stage)}
                        </Pill>
                      </td>
                      <td className="num">{money(a.monthlyVolumeCents)}</td>
                      <td>
                        {a.stage === 'draft' && a.pricing.basisPoints === 0
                          ? 'Not set'
                          : pricingLabel(a.pricing.model, a.pricing.basisPoints)}
                      </td>
                      <td className="num">{age(a.updatedAt)} ago</td>
                      <td>
                        <Link to={`/applications/${a.id}`}>
                          <Button size="sm">
                            {a.stage === 'draft' ? 'Resume' : 'Open'}
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {list.data && list.data.items.length > 0 && view === 'board' && (
            <BoardView items={list.data.items} currentUserId={user?.userId} />
          )}

          {list.data && total > PAGE_SIZE && (
            <div className="pager">
              <span>
                Showing {from + 1}–{from + shown} of {total}
              </span>
              <span className="spacer" />
              <Button
                size="sm"
                disabled={page === 0}
                onClick={() => update({ page: String(page - 1) })}
              >
                Previous
              </Button>
              <Button
                size="sm"
                disabled={from + shown >= total}
                onClick={() => update({ page: String(page + 1) })}
              >
                Next
              </Button>
            </div>
          )}
        </Card>
      </PageBody>
    </>
  );
}

/** The grouped board: same rows, clustered by what each file is waiting on. */
function BoardView({
  items,
  currentUserId,
}: {
  items: ApplicationListItem[];
  currentUserId?: string;
}) {
  const groups: Array<{ key: string; title: string; stages: ApplicationStage[] }> = [
    {
      key: 'action',
      title: 'Needs your action',
      stages: ['conditions', 'rate_exception', 'awaiting_signature'],
    },
    { key: 'uw', title: 'In underwriting', stages: ['in_underwriting', 'submitted'] },
    { key: 'approved', title: 'Approved · boarding', stages: ['approved', 'boarding', 'boarded'] },
    { key: 'draft', title: 'Draft', stages: ['draft'] },
    { key: 'declined', title: 'Declined', stages: ['declined', 'withdrawn'] },
  ];

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    draft: true,
    declined: true,
  });

  return (
    <div className="board">
      {groups.map((g) => {
        const rows = items.filter((i) => g.stages.includes(i.stage));
        if (rows.length === 0) return null;
        const volume = rows.reduce((s, r) => s + r.monthlyVolumeCents, 0);
        const oldest = Math.max(...rows.map((r) => r.ageDays));
        const isCollapsed = collapsed[g.key];

        return (
          <section className="boardgroup" key={g.key}>
            <div className="boardgroup__head">
              <button
                type="button"
                className="treerow__twist"
                aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${g.title}`}
                aria-expanded={!isCollapsed}
                onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
              >
                {isCollapsed ? Icon.chevronRight({ size: 13 }) : Icon.chevronDown({ size: 13 })}
              </button>
              <h3 className="card__title">{g.title}</h3>
              <span className="card__count">{rows.length}</span>
              <span className="spacer" />
              <span className="boardgroup__meta">
                {moneyCompact(volume)} / mo
                {g.key === 'declined'
                  ? ' · reasons visible to L2 and L3 only'
                  : oldest > 0
                    ? ` · oldest ${oldest} day${oldest === 1 ? '' : 's'}`
                    : ''}
              </span>
            </div>

            {!isCollapsed && (
              <div className="table-wrap" style={{ margin: 0, padding: '0 16px 12px' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Owner</th>
                      <th>Status</th>
                      <th className="num">Monthly vol.</th>
                      <th>Pricing</th>
                      <th className="num">Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <Link to={`/applications/${a.id}`} className="cell-primary">
                            {a.dba || 'Untitled draft'}
                          </Link>
                          <div className="cell-sub">
                            {a.reference} · MCC {a.mcc || '—'}
                          </div>
                        </td>
                        <td>
                          {a.submittedByUserId === currentUserId ? 'You' : a.submittedByName}
                        </td>
                        <td>
                          <Pill tone={stageTone(a.stage)}>
                            {a.blockedOn ?? stageLabel(a.stage)}
                          </Pill>
                        </td>
                        <td className="num">{money(a.monthlyVolumeCents)}</td>
                        <td>{pricingLabel(a.pricing.model, a.pricing.basisPoints)}</td>
                        <td className="num">{age(a.lastStageChangeAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
